import React, { useState, useRef, useEffect } from 'react';
import { Trash2 } from 'lucide-react';

/**
 * DeleteButton — two-tap guard for destructive actions.
 *
 * First click arms (turns red + tooltip changes).
 * Second click within 2 s confirms and calls onConfirm.
 * Auto-disarms if the user doesn't follow through.
 *
 * Props:
 *   onConfirm  — called on confirmed deletion (receives the original click event)
 *   className  — layout/positioning classes only (padding, flex, sizing, border-radius)
 *   iconSize   — Tailwind size class for the Trash2 icon (default: 'w-4 h-4')
 *   title      — tooltip for the idle state (default: 'Delete')
 *   testId     — data-testid attribute
 */
const DeleteButton = ({
  onConfirm,
  children,
  className = '',
  iconSize = 'w-4 h-4',
  title = 'Delete',
  testId,
}) => {
  const [armed, setArmed] = useState(false);
  const timerRef = useRef(null);
  useEffect(() => () => clearTimeout(timerRef.current), []);

  const handleClick = (e) => {
    e.stopPropagation();
    if (!armed) {
      setArmed(true);
      timerRef.current = setTimeout(() => setArmed(false), 2000);
    } else {
      clearTimeout(timerRef.current);
      setArmed(false);
      onConfirm(e);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      title={armed ? 'Click again to confirm' : title}
      data-testid={testId}
      className={`transition-all duration-150 rounded ${
        armed
          ? 'text-white bg-rose-500 hover:bg-rose-600'
          : 'text-slate-400 hover:text-rose-600'
      } ${className}`}
    >
      {children ?? <Trash2 className={iconSize} />}
    </button>
  );
};

export default DeleteButton;
